/**
 * venueHeader.js — Adapted for RaceProX
 *
 * Populates the venue header from DB data (circuitAdapter format).
 * No NASCAR_CIRCUITS array or circuit select dropdowns —
 * the circuit is set once from the bootstrap data.
 */
import { applyVenueHeaderFlagFromPresetUrl } from './venueHeaderFlagTexture.js';

/** @type {import('./circuitAdapter.js').HologramCircuit | null} */
let currentCircuit = null;

/**
 * @returns {import('./circuitAdapter.js').HologramCircuit | null}
 */
export function getSelectedCircuit() {
  return currentCircuit;
}

/**
 * @returns {string}
 */
export function getSelectedCircuitId() {
  return currentCircuit?.id ?? '';
}

/**
 * @param {import('./circuitAdapter.js').HologramCircuit} circuit
 * @param {{ silentFlag?: boolean }} [options]
 */
export async function applyVenueCircuit(circuit, { silentFlag = false } = {}) {
  currentCircuit = circuit;
  const titleEl = document.getElementById('venue-header-title');
  const cityEl = document.getElementById('venue-header-city');
  if (titleEl) titleEl.textContent = circuit.title;
  if (cityEl) cityEl.textContent = circuit.location;

  if (circuit.flagUrl) {
    await applyVenueHeaderFlagFromPresetUrl(circuit.flagUrl, circuit.flagLabel, {
      silent: silentFlag,
    });
  }
}

/**
 * Set the organization logo in the header.
 * @param {object | null} organization
 */
export function applyVenueOrganization(organization) {
  const logoImg = /** @type {HTMLImageElement | null} */ (
    document.querySelector('.venue-header__logo img')
  );

  if (logoImg && organization?.logo_url) {
    logoImg.src = organization.logo_url;
    logoImg.alt = organization.name || 'Organization';
  } else if (logoImg) {
    // Hide logo if no org
    logoImg.style.display = 'none';
  }
}

/**
 * Stub — circuit selector init is not needed in the RaceProX version
 * because the circuit is set by the preset. This is kept for compatibility
 * with any main.js code that calls it.
 * @param {{ onCircuitChange?: (circuit: any) => void | Promise<void> }} [_options]
 */
export function initVenueCircuitSelectors(_options = {}) {
  // No-op — circuit selection comes from the preset/bootstrap
}

/**
 * @param {string} _id
 */
export function syncVenueCircuitSelects(_id) {
  // No-op — not needed
}

/**
 * @param {string} _id
 */
export function setSelectedCircuitId(_id) {
  // No-op in the adapted version
}

/**
 * @param {string} _id
 * @param {{ silentFlag?: boolean }} [_options]
 */
export async function applyVenueCircuitById(_id, _options = {}) {
  // No-op — use applyVenueCircuit with a circuit object instead
}

/**
 * Convenience: apply both circuit and organization data from bootstrap.
 * @param {{ circuit: object | null; organization: object | null }} data
 */
export async function applyVenueData({ circuit, organization }) {
  if (circuit) {
    await applyVenueCircuit(circuit, { silentFlag: true });
  }
  applyVenueOrganization(organization);

  // Show or hide the logo element
  const logoImg = /** @type {HTMLImageElement | null} */ (
    document.getElementById('venue-header-logo')
  );
  if (logoImg && organization?.logo_url) {
    logoImg.src = organization.logo_url;
    logoImg.alt = organization.name || 'Organization';
    logoImg.style.display = '';
  } else if (logoImg) {
    logoImg.style.display = 'none';
  }
}
